import is from '@sindresorhus/is';
import type { FunctionComponent, ReactNode } from 'react';
import { useMemo, useRef } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import { ScrollArea } from '~/ui/core/scroll-area/ScrollArea';
import { Spinner } from '~/ui/core/spinner/Spinner';
import { AssistantBubble } from '~/ui/domain/chat/AssistantBubble';
import { ThoughtMarker } from '~/ui/domain/chat/ThoughtMarker';
import { UserBubble } from '~/ui/domain/chat/UserBubble';

import type { AgentChatMessage, AgentChatMessagePage, AssistantMessageEntry } from '../../data-contract/chat';
import { useScrollToBottom } from '../hooks/use-scroll-to-bottom';
import { renderInlineNode } from '../structured-renderers';

type ChatMessageListProps = {
    readonly isLoading: boolean;
    readonly pages: readonly AgentChatMessagePage[];
};

const getUserText = (message: AgentChatMessage): string => {
    if (!is.undefined(message.text)) return message.text;
    if (is.undefined(message.entries)) return '';
    return message.entries
        .filter((entry) => entry.type === 'text')
        .map((entry) => entry.text)
        .join('');
};

const renderEntry = (
    entry: AssistantMessageEntry,
    index: number,
    isStreaming: boolean,
    isLastEntry: boolean,
): ReactNode => {
    if (entry.type === 'text') {
        if (entry.text.length === 0) return null;
        return (
            <AssistantBubble.Text key={`text-${index}`} streaming={isStreaming && isLastEntry}>
                {entry.text}
            </AssistantBubble.Text>
        );
    }
    const node = renderInlineNode(entry.envelope, { interactive: true });
    if (is.nullOrUndefined(node)) return null;
    return <AssistantBubble.Extras key={entry.envelope.eventId}>{node}</AssistantBubble.Extras>;
};

const renderAssistantMessage = (message: AgentChatMessage, errorMessage: string | null): ReactNode => {
    if (!is.null(errorMessage)) {
        return (
            <AssistantBubble key={message.id}>
                <span className="text-error-700">{errorMessage}</span>
            </AssistantBubble>
        );
    }

    const entries = message.entries ?? [];
    const isStreaming = message.status === 'pending';

    if (entries.length === 0) {
        return isStreaming ? <ThoughtMarker key={message.id}>{null}</ThoughtMarker> : null;
    }

    return (
        <AssistantBubble key={message.id} streaming={isStreaming}>
            {entries.map((entry, index) => renderEntry(entry, index, isStreaming, index === entries.length - 1))}
        </AssistantBubble>
    );
};

export const ChatMessageList: FunctionComponent<ChatMessageListProps> = ({ isLoading, pages }) => {
    const translate = useTranslate();
    const scrollRef = useRef<HTMLDivElement>(null);

    const messages = useMemo<readonly AgentChatMessage[]>(() => {
        const flattened = pages.flatMap((page) => page.messages);
        return [...flattened].reverse();
    }, [pages]);

    useScrollToBottom(scrollRef, [messages]);

    return (
        <ScrollArea viewportRef={scrollRef} className="min-h-0 w-full flex-1">
            <div className="flex min-h-0 w-full flex-1 flex-col gap-l p-xxl py-l">
                {isLoading && messages.length === 0 ? (
                    <div className="flex flex-1 items-center justify-center">
                        <Spinner className="h-l w-l" />
                    </div>
                ) : null}
                {messages.map((message) =>
                    message.role === 'user' ? (
                        <UserBubble key={message.id}>{getUserText(message)}</UserBubble>
                    ) : (
                        renderAssistantMessage(
                            message,
                            message.status === 'error' ? translate('chat.error-fallback') : null,
                        )
                    ),
                )}
            </div>
        </ScrollArea>
    );
};
